/**
 * Shared constants for the reporting feature
 */
import { formatIsoWeekFullLabel } from './isoWeek';

/** Locked height for dashboard grid tiles so wrapping titles cannot stagger a row. */
export const DASHBOARD_CHART_TILE_HEIGHT_CLASS = 'h-[15rem]';
export const DASHBOARD_CLASSIC_TILE_HEIGHT_CLASS = 'h-[16.5rem] max-sm:h-auto max-sm:min-h-[16.5rem]';

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// Fiscal year quarters (matches the backend: fiscal year starts April).
// Q1 = Apr–Jun (representative month 6), Q4 = Jan–Mar (representative month 3).
export type PeriodGrain = 'week' | 'month' | 'quarter';

export const FISCAL_QUARTERS = [
  { value: 1, representativeMonth: 6, rangeLabel: 'Apr–Jun' },
  { value: 2, representativeMonth: 9, rangeLabel: 'Jul–Sep' },
  { value: 3, representativeMonth: 12, rangeLabel: 'Oct–Dec' },
  { value: 4, representativeMonth: 3, rangeLabel: 'Jan–Mar' }
] as const;

export const MONTH_TO_FISCAL_QUARTER: Record<number, number> = {
  4: 1,
  5: 1,
  6: 1,
  7: 2,
  8: 2,
  9: 2,
  10: 3,
  11: 3,
  12: 3,
  1: 4,
  2: 4,
  3: 4
};

/**
 * Format a period for display given the card's cadence.
 *
 * For quarterly cadence we render "Q<n> FY<yy>" rather than the
 * representative-month name, because seeing "June 2026" on a quarterly card
 * routinely confuses viewers into thinking it's a monthly figure.
 *
 * Fiscal-year convention: FY26 = the fiscal year ending March 2026, which runs
 * Apr 2025 → Mar 2026.
 *
 * For weekly cadence, `month` is ignored and `week` (the ISO 8601 week
 * number) is used instead - mirroring the DataValue storage convention
 * where a weekly row leaves period_month null.
 */
export function formatPeriodLabel(
  year: number,
  month: number,
  cadence: 'monthly' | 'quarterly' | 'weekly' | null | undefined,
  week?: number | null
): string {
  if (cadence === 'weekly' && week != null) {
    return formatIsoWeekFullLabel(year, week);
  }
  if (cadence === 'quarterly') {
    const quarter = MONTH_TO_FISCAL_QUARTER[month] ?? 1;
    // Q4 (Jan–Mar) falls inside the fiscal year named after the calendar year
    // of that quarter's end month. Earlier quarters live in the same FY but
    // their calendar year is FY - 1.
    const fiscalYear = quarter === 4 ? year : year + 1;
    return `Q${quarter} FY${String(fiscalYear).slice(-2)}`;
  }
  return `${MONTHS[month - 1]} ${year}`;
}

export function getFiscalQuarterFromMonth(month: number): 1 | 2 | 3 | 4 {
  return (MONTH_TO_FISCAL_QUARTER[month] ?? 1) as 1 | 2 | 3 | 4;
}

export function representativeMonthForQuarter(quarter: 1 | 2 | 3 | 4): number {
  return FISCAL_QUARTERS[quarter - 1].representativeMonth;
}

export function stepFiscalQuarter(year: number, month: number, delta: -1 | 1): { year: number; month: number } {
  const q = getFiscalQuarterFromMonth(month);
  if (delta === 1) {
    if (q === 1) return { year, month: 9 };
    if (q === 2) return { year, month: 12 };
    if (q === 3) return { year: year + 1, month: 3 };
    return { year, month: 6 };
  }
  if (q === 1) return { year, month: 3 };
  if (q === 2) return { year, month: 6 };
  if (q === 3) return { year, month: 9 };
  return { year: year - 1, month: 12 };
}

export function stepCalendarMonth(year: number, month: number, delta: -1 | 1): { year: number; month: number } {
  const next = month + delta;
  if (next < 1) return { year: year - 1, month: 12 };
  if (next > 12) return { year: year + 1, month: 1 };
  return { year, month: next };
}
